// ============================================================
// THE SURFACES ON THE DISPLAY (plan design-gaps, T3; the terrain page:
// "surfaces are one region each, with one outline and one label"). A painted
// surface is meshed into flat, labelled solids (zones.ts `meshZone`, a rect
// each); here they are read back as regions by their label - one label each,
// on the ground at its largest rect - and a figure's feet are asked which one
// they stand in, so the display can play its ENTRY as it steps in.
//
// Pure. Zero React dependency. Metres.
// ============================================================

import type { Solid, Vec3 } from '../battlefield/types';
import { containsPoint } from '../battlefield/lineOfSight';
import { solidGeometry } from '../battlefield/solids';

/** The painted surfaces among the solids: flat and labelled (a hidden one only for whoever may see it). */
export const surfacesOf = (solids: readonly Solid[], includeHidden: boolean): Solid[] =>
  solids.filter((s) => s.flat && s.label && (includeHidden || !s.hidden));

/** One label per surface, on the ground at the middle of its largest rect, in its own colour. */
export function surfaceLabels(surfaces: readonly Solid[]): { label: string; colour: string | null; at: Vec3 }[] {
  const largest = new Map<string, Solid>();
  for (const s of surfaces) {
    const was = largest.get(s.label!);
    if (!was || areaOf(s) > areaOf(was)) largest.set(s.label!, s);
  }
  return [...largest.values()].map((s) => ({
    label: s.label!.toUpperCase(),
    colour: s.colour ?? null,
    at: { x: s.position.x, y: s.position.y + s.height, z: s.position.z },
  }));
}

const areaOf = (s: Solid) => s.scaleX * s.scaleZ * s.radiusTop * s.radiusTop;

/** The surface a figure's feet stand in, or null on bare ground. */
export function surfaceUnder(feet: Vec3, surfaces: readonly Solid[]): Solid | null {
  const probe = { x: feet.x, y: feet.y + 0.01, z: feet.z };
  return surfaces.find((s) => containsPoint(solidGeometry(s), probe)) ?? null;
}
